import EventEmitter from '../../helpers/EventEmmiter';

/**
 * Manages pre-recorded audio playback for museum guide points.
 *
 * Events:
 *   'play'        — audio started,   payload: { url }
 *   'ended'       — finished naturally (no interruption)
 *   'interrupted' — stopped because the user started speaking
 *   'error'       — playback failed,  payload: error
 */
class MuseumAudioPlayer extends EventEmitter {
  /**
   * @param {HTMLAudioElement|null} sharedAudio
   *   A pre-unlocked Audio element created during a user gesture (required on
   *   iOS Safari — new Audio() elements created outside a gesture are blocked
   *   by autoplay policy). When provided, every play() call reuses this element
   *   by updating its src instead of creating a new instance.
   */
  constructor(sharedAudio = null) {
    super();
    this._shared = sharedAudio; // never nulled — reused across plays
    this._audio = null;         // active playback reference (may equal _shared)
    this._isPlaying = false;
    this._interrupted = false;
  }

  get isPlaying() {
    return this._isPlaying;
  }

  _getElement(url) {
    if (this._shared) {
      this._shared.src = url;
      this._shared.currentTime = 0;
      return this._shared;
    }
    return new Audio(url);
  }

  play(url) {
    this.stop();
    if (!url || typeof window === 'undefined') return;
    this._interrupted = false;

    const audio = this._getElement(url);
    this._audio = audio;
    this._isPlaying = true;

    audio.ontimeupdate = () => {
      if (this._audio !== audio) return;
      const duration = audio.duration;
      if (duration && duration > 0) {
        this._fireEvent('timeupdate', { progress: audio.currentTime / duration });
      }
    };

    audio.onended = () => {
      if (this._audio !== audio) return; // stale reference
      this._isPlaying = false;
      this._audio = null;
      if (!this._interrupted) {
        this._fireEvent('ended');
      }
    };

    audio.onerror = (err) => {
      if (this._audio !== audio) return;
      this._isPlaying = false;
      this._audio = null;
      this._fireEvent('error', err);
    };

    audio.play().catch(err => {
      if (this._audio !== audio) return;
      this._isPlaying = false;
      this._audio = null;
      this._fireEvent('error', err);
    });

    this._fireEvent('play', { url });
  }

  /**
   * Called when the user starts speaking mid-playback.
   * @returns {boolean} true if audio was actually interrupted
   */
  interrupt() {
    if (!this._isPlaying || !this._audio) return false;
    this._interrupted = true;
    try { this._audio.pause(); } catch (_) {}
    this._isPlaying = false;
    this._audio = null;
    this._fireEvent('interrupted');
    return true;
  }

  stop() {
    if (!this._audio) return;
    try { this._audio.pause(); } catch (_) {}
    this._isPlaying = false;
    this._audio = null;
    // Note: this._shared is intentionally NOT nulled — it is reused across plays
  }
}

export default MuseumAudioPlayer;
